import React from "react";
import { useNavigate } from "react-router-dom";
import { useNavigation } from "@core/context/NavigationContext";
import { cn } from "@shared/lib/utils";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@shared/ui/card";
import { InfoTooltip } from "@shared/ui/infoTooltip";
import { Switch } from "@shared/ui/switch";
import { Building } from "lucide-react";

interface UtilityCardProps {
  id: string;
  name: string;
  description: string;
  isActive: boolean;
  setupProgress: number;
  className?: string;
  onActivateToggle?: (id: string, isActive: boolean) => void;
  onEditUtility?: (id: string) => void;
  utility: any; 
}

const UtilityCard: React.FC<UtilityCardProps> = ({
  id,
  name,
  description,
  isActive,
  setupProgress,
  className,
  onActivateToggle,
  onEditUtility,
  utility
}) => {
  console.log("UtilityCard rendered with description:", description);
  const navigate = useNavigate();
  const { setCurrentModule } = useNavigation();

  const handleToggleActive = (checked: boolean) => {
    onActivateToggle?.(id, checked);
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onEditUtility?.(id);
  };

  // const handleCardClick = () => {
  
  // navigate("/onboarding/utility-setup/utility-admin-dashboard");
  // };
  const handleCardClick = () => {
  // Save full utility object
  localStorage.setItem("utility", JSON.stringify(utility));

  // Hard redirect (preserves reload)
  window.location.href = "/utility/utility-setup/utility-admin-dashboard";
};

  return (
    <Card className={cn("overflow-hidden transition-all duration-300 hover:shadow-md h-full relative", className)}>
      {/* Header */}
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center space-x-2">
            <div className="p-2 rounded-md bg-primary/10 text-primary">
              <Building className="h-5 w-5" />
            </div>
            <CardTitle className="text-lg font-medium">{name}</CardTitle>
          </div>
          
          <div className="flex flex-col items-end gap-1">
            <Switch
              checked={isActive}
              onCheckedChange={handleToggleActive}
              className={isActive ? "data-[state=checked]:bg-green-500" : ""}
              aria-label={`${isActive ? "Deactivate" : "Activate"} utility`}
            />
            <Badge
              variant={isActive ? "default" : "outline"}
              className={cn(
                "text-xs px-2 py-0.5 rounded-full font-semibold",
                isActive
                  ? "bg-green-100 text-green-700 border-green-300"
                  : "bg-gray-100 text-gray-700 border-gray-300"
              )}
            >
              {isActive ? "Active" : "Inactive"}
            </Badge>
          </div>
        </div>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>

      {/* Content - Progress */}
      <CardContent className="pb-2">
        <div className="flex justify-between items-center text-sm mb-2">
          <div className="flex items-center gap-1">
            <span>Setup Progress:</span>
            <InfoTooltip content="Complete all required configuration for this utility" />
          </div>
          <span className="font-medium">{setupProgress}%</span>
        </div>
        
        <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500 bg-primary"
            style={{ width: `${setupProgress}%` }}
          />
        </div>
      </CardContent>

      {/* Footer - Actions */}
      <CardFooter className="flex gap-2 mt-2">
        <Button
          variant="outline"
          size="sm"
          className="flex-1 text-xs"
          onClick={handleEdit}
        >
          Edit
        </Button>
        
        <Button
           variant="outline"
          size="sm"
          className="flex-1 text-xs"
          onClick={handleCardClick}
        >
          {setupProgress === 100 ? "Manage Utility" : "Continue Setup"}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default UtilityCard;